"use client";

import type { FC, KeyboardEvent } from "react";
import { useId, useState } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import * as RadixPopover from "@radix-ui/react-popover";
import { LocateFixedIcon } from "@/components/icons";
import { FieldLabel, FieldMessage, POPOVER_PANEL_WIDTH, type FieldState } from "@/components/input/field-chrome";
import { cn } from "@/lib/cn";
import { useCurrentLocation, usePlaceAutocomplete } from "../../hooks";
import type { LocationSelection } from "../../lib";

type AddressAutocompleteInputProps = {
  className?: string;
  label?: string;
  message?: string;
  state?: FieldState;
  required?: boolean;
  placeholder?: string;
  id?: string;
  defaultValue?: string;
  value?: string;
  /** Fired both when a suggestion is selected and when the GPS button resolves a position, so
   *  callers don't need to branch on how the address was chosen. */
  onLocationChange: (selection: LocationSelection) => void;
  onClear?: () => void;
};

/** Matches the `DirectorySearchHero` search-bar field (Figma 1:2656 desktop, 1:1474 mobile) —
 *  map-pin + text on the left, a divider, then a GPS "Near Me" trigger. */
export const AddressAutocompleteInput: FC<AddressAutocompleteInputProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  placeholder = "Suburb or postcode",
  id,
  defaultValue = "",
  value,
  onLocationChange,
  onClear,
}) => {
  const [text, setText] = useState(defaultValue);
  const [syncedValue, setSyncedValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const { suggestions, search, select, clear } = usePlaceAutocomplete();
  const { locate, isLocating } = useCurrentLocation();
  const listboxId = useId();
  const isOpen = open && suggestions.length > 0;

  if (value !== undefined && value !== syncedValue) {
    setSyncedValue(value);
    setText(value);
  }

  const commit = (result: LocationSelection) => {
    setText(result.address);
    setSyncedValue(result.address);
    onLocationChange(result);
  };

  const handleChange = (next: string) => {
    setText(next);
    search(next);
    setOpen(true);
    setHighlightedIndex(0);

    if (!next.trim()) {
      setSyncedValue("");
      onClear?.();
    }
  };

  const handleSelect = async (suggestion: (typeof suggestions)[number]) => {
    setOpen(false);
    const result = await select(suggestion);
    if (!result) return;
    commit(result);
  };

  const handleLocate = async () => {
    setOpen(false);
    clear();
    const result = await locate();
    if (!result) return;
    commit(result);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((index) => Math.min(index + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case "Enter": {
        const suggestion = suggestions[highlightedIndex];
        if (suggestion) {
          event.preventDefault();
          void handleSelect(suggestion);
        }
        break;
      }
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={id} state={state} required={required}>
        {label}
      </FieldLabel>
      <RadixPopover.Root open={isOpen} onOpenChange={setOpen}>
        <RadixPopover.Anchor asChild>
          <div
            className={cn(
              "flex h-72 w-full items-center gap-16 rounded-medium border bg-neutral-white px-16 transition-colors focus-within:border-brand-blue lg:h-auto lg:justify-between lg:gap-8 lg:p-24",
              state === "error" ? "border-brand-red" : "border-neutral-light-grey",
              className,
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-12 lg:gap-8">
              <MapPinIcon aria-hidden className="size-24 shrink-0 text-brand-blue lg:size-28" />
              <input
                id={id}
                value={text}
                placeholder={placeholder}
                required={required}
                autoComplete="off"
                onChange={(event) => handleChange(event.target.value)}
                onFocus={() => setOpen(suggestions.length > 0)}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={isOpen ? `${listboxId}-${highlightedIndex}` : undefined}
                className="min-w-0 flex-1 bg-transparent text-14 font-medium leading-[1.4] text-neutral-black outline-none placeholder:text-neutral-black/50 lg:text-18"
              />
            </div>
            <span aria-hidden className="h-32 w-px shrink-0 bg-neutral-black/50 lg:h-41" />
            <button
              type="button"
              onClick={() => void handleLocate()}
              disabled={isLocating}
              aria-label="Use my current location"
              className="group flex shrink-0 cursor-pointer items-center gap-8 text-18 font-medium leading-[1.4] text-neutral-black disabled:opacity-50 lg:gap-12"
            >
              <LocateFixedIcon
                aria-hidden
                className={cn("size-24 shrink-0 text-brand-blue lg:size-28", isLocating && "animate-pulse")}
              />
              <span className="hidden lg:inline">Near Me</span>
            </button>
          </div>
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <RadixPopover.Content
            align="start"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className={cn(
              "pointer-events-auto z-50 flex flex-col gap-4 rounded-small border border-neutral-light-grey bg-neutral-white p-8 shadow-lg data-[state=closed]:animate-[dropdown-out_150ms_ease-in] data-[state=open]:animate-[dropdown-in_150ms_ease-out]",
              POPOVER_PANEL_WIDTH,
            )}
          >
            <div id={listboxId} role="listbox">
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion.placeId}
                  id={`${listboxId}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => void handleSelect(suggestion)}
                  className={cn(
                    "w-full cursor-pointer rounded-small px-16 py-12 text-left text-16 text-neutral-black outline-none transition-colors duration-100",
                    index === highlightedIndex && "bg-tone-aqua-tint",
                  )}
                >
                  {suggestion.text}
                </button>
              ))}
            </div>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
      <FieldMessage state={state}>{message}</FieldMessage>
    </div>
  );
};
